import type { HttpMethod } from '../http/types.ts';

/**
 * The `/authorize_token` scope grammar, as data rather than as strings sprinkled through the app.
 *
 * Grammar (§8): `METHOD1;METHOD2…:ENDPOINT(*)?` — e.g. `GET;POST:subscriptions*`. An *empty*
 * method list is legal and means "any method": the instance's own CSRF tokens carry `:signout`,
 * `:playlist_ajax` and friends in exactly that shape.
 *
 * Modelling it lets the app answer a question it otherwise cannot: when the instance refuses a
 * call with `Invalid scope`, was that because our grant is genuinely too narrow (re-authorize with
 * more) or because we asked for a path the token was never meant to reach (a bug)? Guessing there
 * means re-running the consent flow on every 403, which is exactly the loop §3 warns about.
 */
export interface AuthScope {
  /** Empty means *all* methods, which is how the instance itself writes `:signout`. */
  readonly methods: readonly HttpMethod[];
  /** The path under `/api/v1/auth/`, with no leading slash — `subscriptions`, `playlists/IVPAAA`. */
  readonly endpoint: string;
  /** A trailing `*`, which extends the grant to everything below `endpoint`. */
  readonly wildcard: boolean;
}

const METHODS: readonly HttpMethod[] = ['GET', 'POST', 'PATCH', 'DELETE', 'HEAD'];

function asMethod(input: string): HttpMethod | null {
  const upper = input.trim().toUpperCase();
  return METHODS.find((m) => m === upper) ?? null;
}

export function formatAuthScope(scope: AuthScope): string {
  return `${scope.methods.join(';')}:${scope.endpoint}${scope.wildcard ? '*' : ''}`;
}

/** Total: anything that is not a well-formed scope returns `null` rather than throwing. */
export function parseAuthScope(input: string): AuthScope | null {
  const raw = input.trim();
  // Split on the *last* colon: an endpoint never contains one, but a method list always precedes one.
  const colon = raw.lastIndexOf(':');
  if (colon === -1) return null;

  const methodPart = raw.slice(0, colon);
  let endpoint = raw.slice(colon + 1);
  if (endpoint === '') return null;

  const wildcard = endpoint.endsWith('*');
  if (wildcard) endpoint = endpoint.slice(0, -1);
  if (endpoint.startsWith('/')) endpoint = endpoint.slice(1);

  const methods: HttpMethod[] = [];
  if (methodPart !== '') {
    for (const piece of methodPart.split(';')) {
      const method = asMethod(piece);
      if (method === null) return null;
      methods.push(method);
    }
  }

  return { methods, endpoint, wildcard };
}

/** The instance takes the scope list as one **comma-separated** query parameter (verified on the wire). */
export function formatScopesParam(scopes: readonly AuthScope[]): string {
  return scopes.map(formatAuthScope).join(',');
}

const AUTH_PREFIX = '/api/v1/auth/';

/**
 * `/api/v1/auth/subscriptions/UC…` → `subscriptions/UC…`. Returns `null` for a path that is not an
 * authenticated endpoint at all, so "this call needs no token" stays distinguishable from
 * "this call needs a token we don't have".
 */
export function authEndpointOf(urlOrPath: string): string | null {
  const start = urlOrPath.indexOf(AUTH_PREFIX);
  if (start === -1) return null;
  const rest = urlOrPath.slice(start + AUTH_PREFIX.length);
  const cut = rest.search(/[?#]/);
  return cut === -1 ? rest : rest.slice(0, cut);
}

function endpointMatches(scope: AuthScope, endpoint: string): boolean {
  if (scope.endpoint === endpoint) return true;
  if (!scope.wildcard) return false;
  // `subscriptions*` covers `subscriptions/UC…`, but must not also cover `subscriptions_other`.
  return endpoint.startsWith(scope.endpoint);
}

export function scopeAllows(scope: AuthScope, method: HttpMethod, endpoint: string): boolean {
  if (scope.methods.length > 0 && !scope.methods.includes(method)) return false;
  return endpointMatches(scope, endpoint);
}

/** Would this grant permit that call? Verified against the instance, per method *and* per endpoint. */
export function scopesAllow(
  scopes: readonly AuthScope[],
  method: HttpMethod,
  endpoint: string,
): boolean {
  return scopes.some((scope) => scopeAllows(scope, method, endpoint));
}

/**
 * R9's enumerated least-privilege set. Deliberately **not** `:*`, and deliberately **not**
 * `POST:authorize_token` — a token that can mint tokens is a token that cannot be meaningfully
 * revoked by narrowing.
 *
 * `tokens*` is absent from the *default* ask, and since 2026-08-02 that is a choice the reader can
 * override rather than only a trade-off: the sign-in page's automatic-renewal option adds
 * `POST:tokens*` (`auth/renew.ts` — measured to mean "mint successors within these same scopes and
 * end itself", not "end other sessions"). Without it the app cannot server-side-revoke its own
 * session, so signing out stays local-only either way — `GET:tokens` (the account's whole session
 * list) is never asked for.
 */
export const VISIOUS_SCOPES: readonly AuthScope[] = [
  { methods: ['GET', 'POST', 'DELETE'], endpoint: 'subscriptions', wildcard: true },
  { methods: ['GET', 'POST', 'PATCH', 'DELETE'], endpoint: 'playlists', wildcard: true },
  { methods: ['GET'], endpoint: 'feed', wildcard: true },
  { methods: ['GET', 'POST'], endpoint: 'preferences', wildcard: false },
  { methods: ['GET', 'POST', 'DELETE'], endpoint: 'history', wildcard: true },
];
